import type { CaregiverMinified } from '@api/READ_ONLY/caregiver_api/Api';
import type { Client } from '@api/READ_ONLY/client_api/Api';
import FormElement from '@app/components/ui/FormElement';
import CaregiversSelectGeneric from '@feat-caregivers/components/CaregiversSelect/CaregiversSelectGeneric';
import ClientSelectGeneric from '@feat-clients/components/ClientSelect/ClientSelectGeneric';
import useSmartErrorHandling from '@hooks/useSmartErrorHandling';
import { Alert, App, DatePicker, Input, InputNumber, Modal, Radio, Select, Typography } from 'antd';
import MoneyInput from '@app/components/common/MoneyInput';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { createMovement } from './api';
import { useInventoryMoneyVisibility } from './moneyVisibility';
import { movementTypesForItem, stockSign } from './movementTypes';
import type {
  InventoryItem,
  InventoryMovementCreate,
  InventoryMovementType,
  InventoryRecipientType,
  RentalRateUnit,
} from './types';
import { useDecimalInputProps } from '@app/components/common/DecimalInput';

export interface RecordMovementModalProps {
  open: boolean;
  item: InventoryItem | null;
  onClose: () => void;
  /** Called after a successful movement so the caller can refresh the list. */
  onSaved: () => void;
}

const RECIPIENT_TYPES: InventoryRecipientType[] = ['client', 'caregiver', 'external', 'internal'];
const RENTAL_RATE_UNITS: RentalRateUnit[] = ['day', 'week', 'month'];

const DATE_FORMAT = 'YYYY-MM-DD';

/** Which field an error belongs to, so it renders under that field and not in a list. */
type FieldErrors = Partial<
  Record<
    | 'quantity'
    | 'unitCost'
    | 'unitPrice'
    | 'rentalRate'
    | 'rentalStart'
    | 'rentalExpectedReturn'
    | 'adjustmentSign'
    | 'recipient',
    string
  >
>;

const RecordMovementModal = ({ open, item, onClose, onSaved }: RecordMovementModalProps) => {
  const { t } = useTranslation();
  // Money the user is not shown is not asked for or sent: the server takes the item's default.
  const { showCost, showPrices } = useInventoryMoneyVisibility();
  // NUMERIC(12,2) in the database; a quantity can pass a thousand, so separators may group.
  const quantityProps = useDecimalInputProps({ precision: 2, grouping: true });
  const { message } = App.useApp();
  const { handleError } = useSmartErrorHandling();

  // Only the types this item can actually take (see movementTypes.ts).
  const availableTypes = useMemo(() => movementTypesForItem(item), [item]);

  const [movementType, setMovementType] = useState<InventoryMovementType>('purchase');
  const [quantity, setQuantity] = useState<number | null>(null);
  const [unitCost, setUnitCost] = useState<number | null>(null);
  const [supplier, setSupplier] = useState('');
  const [unitPrice, setUnitPrice] = useState<number | null>(null);
  const [rentalRate, setRentalRate] = useState<number | null>(null);
  const [rentalRateUnit, setRentalRateUnit] = useState<RentalRateUnit>('day');
  const [rentalStart, setRentalStart] = useState<Dayjs | null>(null);
  const [rentalExpectedReturn, setRentalExpectedReturn] = useState<Dayjs | null>(null);
  const [recipientType, setRecipientType] = useState<InventoryRecipientType>('client');
  const [recipientId, setRecipientId] = useState<number | null>(null);
  const [recipientName, setRecipientName] = useState('');
  // The picked entity is kept alongside its id so the select can render its
  // label without refetching (GenericSearchSelect's selectedItemsProvider).
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedCaregiver, setSelectedCaregiver] = useState<CaregiverMinified | null>(null);
  const [adjustmentSign, setAdjustmentSign] = useState<number | null>(1);
  const [notes, setNotes] = useState('');
  const [occurredAt, setOccurredAt] = useState<Dayjs | null>(null);

  const [errors, setErrors] = useState<FieldErrors>({});
  const [isSaving, setIsSaving] = useState(false);

  const clearError = useCallback((field: keyof FieldErrors) => {
    setErrors((current) => {
      if (current[field] === undefined) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  }, []);

  // Seed the form whenever the modal opens: reset every field and prefill the
  // pricing inputs from the item's configured defaults where available.
  useEffect(() => {
    if (!open) return;
    setMovementType('purchase');
    setQuantity(null);
    setUnitCost(item?.defaultCost ?? null);
    setSupplier('');
    setUnitPrice(item?.defaultSalePrice ?? null);
    setRentalRate(item?.defaultRentalRate ?? null);
    setRentalRateUnit(item?.defaultRentalRateUnit ?? 'day');
    setRentalStart(null);
    setRentalExpectedReturn(null);
    setRecipientType('client');
    setRecipientId(null);
    setRecipientName('');
    setSelectedClient(null);
    setSelectedCaregiver(null);
    setAdjustmentSign(1);
    setNotes('');
    setOccurredAt(null);
    setErrors({});
  }, [open, item]);

  const showsRecipient = movementType === 'sale' || movementType === 'give' || movementType === 'rental_out';
  const showsChargeInfo = (movementType === 'sale' || movementType === 'rental_out') && recipientType === 'client';

  const unitLabel = item?.unit?.trim() || '';
  const onHand = item?.onHand ?? 0;
  const sign = stockSign(movementType, adjustmentSign);
  // Live preview of where this movement leaves the stock, which is what makes
  // "in stock" mean something: the figure alone read as an unexplained 0.
  const resultingStock = onHand + sign * (quantity ?? 0);
  const drawsDownStock = sign < 0;
  const exceedsStock = drawsDownStock && quantity != null && quantity > 0 && resultingStock < 0;

  const handleTypeChange = useCallback((value: InventoryMovementType) => {
    setMovementType(value);
    setErrors({});
  }, []);

  const handleClientChange = useCallback(
    (selection: Client | Client[] | undefined) => {
      const client = Array.isArray(selection) ? selection[0] : selection;
      setSelectedClient(client ?? null);
      setRecipientId(client?.clientId ?? null);
      clearError('recipient');
    },
    [clearError]
  );

  const handleCaregiverChange = useCallback(
    (selection: CaregiverMinified | CaregiverMinified[] | undefined) => {
      const caregiver = Array.isArray(selection) ? selection[0] : selection;
      setSelectedCaregiver(caregiver ?? null);
      setRecipientId(caregiver?.caregiverId ?? null);
      clearError('recipient');
    },
    [clearError]
  );

  // Money this user cannot enter comes from the item's default; without one only an
  // administrator can record the movement. `=== false`: an older backend sends no flag.
  const needsAdminPrice =
    (movementType === 'purchase' && !showCost && item?.hasDefaultCost === false) ||
    (movementType === 'sale' && !showPrices && item?.hasDefaultSalePrice === false) ||
    (movementType === 'rental_out' && !showPrices && item?.hasDefaultRentalRate === false);

  // Client-side validation mirroring the backend `validate_movement`. Returns the
  // errors keyed by the field they belong to, so each renders under its own input.
  const validate = (): FieldErrors => {
    const found: FieldErrors = {};

    if (quantity == null || quantity <= 0) {
      found.quantity = t('inventory-validation-quantity-required');
    } else if (exceedsStock) {
      found.quantity = t('inventory-validation-insufficient-stock', { available: onHand });
    }

    if (showCost && movementType === 'purchase' && (unitCost == null || unitCost < 0)) {
      found.unitCost = t('inventory-validation-unit-cost-required');
    }
    if (showPrices && movementType === 'sale' && (unitPrice == null || unitPrice < 0)) {
      found.unitPrice = t('inventory-validation-unit-price-required');
    }
    if (movementType === 'rental_out') {
      if (showPrices && (rentalRate == null || rentalRate < 0)) {
        found.rentalRate = t('inventory-validation-rental-rate-required');
      }
      if (!rentalStart) found.rentalStart = t('inventory-validation-rental-start-required');
      if (!rentalExpectedReturn) {
        found.rentalExpectedReturn = t('inventory-validation-rental-expected-return-required');
      } else if (rentalStart && rentalExpectedReturn.isBefore(rentalStart, 'day')) {
        found.rentalExpectedReturn = t('inventory-validation-rental-return-before-start');
      }
    }
    if (movementType === 'adjustment' && adjustmentSign == null) {
      found.adjustmentSign = t('inventory-validation-adjustment-sign-required');
    }

    if (showsRecipient) {
      if (recipientType === 'client' && recipientId == null) {
        found.recipient = t('inventory-validation-recipient-client-required');
      } else if (recipientType === 'caregiver' && recipientId == null) {
        found.recipient = t('inventory-validation-recipient-caregiver-required');
      } else if (recipientType === 'external' && !recipientName.trim()) {
        found.recipient = t('inventory-validation-recipient-name-required');
      }
    }

    return found;
  };

  const handleSave = async () => {
    if (item == null) return;

    const found = validate();
    if (Object.keys(found).length > 0) {
      setErrors(found);
      return;
    }

    // Build the create body with only the fields relevant to the chosen type.
    const body: InventoryMovementCreate = {
      itemId: item.itemId,
      movementType,
      quantity: quantity as number,
    };

    if (movementType === 'purchase') {
      if (showCost) body.unitCost = unitCost;
      if (supplier.trim()) body.supplier = supplier.trim();
    } else if (movementType === 'sale') {
      if (showPrices) body.unitPrice = unitPrice;
    } else if (movementType === 'rental_out') {
      if (showPrices) {
        body.rentalRate = rentalRate;
        body.rentalRateUnit = rentalRateUnit;
      }
      body.rentalStart = rentalStart ? rentalStart.format(DATE_FORMAT) : null;
      body.rentalExpectedReturn = rentalExpectedReturn ? rentalExpectedReturn.format(DATE_FORMAT) : null;
    } else if (movementType === 'adjustment') {
      body.adjustmentSign = adjustmentSign;
    }

    if (showsRecipient) {
      body.recipientType = recipientType;
      if (recipientType === 'client' || recipientType === 'caregiver') {
        body.recipientId = recipientId;
      } else if (recipientType === 'external') {
        body.recipientName = recipientName.trim();
      }
    }

    if (notes.trim()) body.notes = notes.trim();
    if (occurredAt) body.occurredAt = occurredAt.format(DATE_FORMAT);

    try {
      setIsSaving(true);
      await createMovement(body);
      message.success(t('inventory-movement-success'));
      onSaved();
      onClose();
    } catch (e) {
      handleError(e as Error, { customMessage: t('inventory-movement-failed') });
    } finally {
      setIsSaving(false);
    }
  };

  // The unit is free text, so it can be anything — including something numeric
  // ("100"). Rendering it small and muted keeps "50 100" from reading as 50100.
  const renderStock = (value: number, className: string) => (
    <span className={className}>
      <span className="tabular-nums">{value}</span>
      {unitLabel && <span className="ml-1 text-xs font-normal text-gray-500">{unitLabel}</span>}
    </span>
  );

  return (
    <Modal
      open={open}
      width={560}
      title={
        item ? t('inventory-record-movement-title-for', { name: item.name }) : t('inventory-record-movement-title')
      }
      onCancel={isSaving ? undefined : onClose}
      onOk={handleSave}
      okText={t('common-save')}
      cancelText={t('common-cancel')}
      confirmLoading={isSaving}
      okButtonProps={{ disabled: needsAdminPrice }}
      maskClosable={!isSaving}
      destroyOnHidden
    >
      <div className="flex flex-col gap-3 pt-2">
        {/* Stock panel. The old modal showed "0 in stock" under the quantity box
            with nothing to say what it counted or where the movement would leave it. */}
        <div className="flex flex-wrap gap-8 rounded-md bg-gray-50 px-4 py-3">
          <div>
            <div className="text-xs text-gray-500">{t('inventory-movement-stock-now')}</div>
            <div>{renderStock(onHand, 'text-base font-semibold')}</div>
          </div>
          {sign !== 0 && (
            <div>
              <div className="text-xs text-gray-500">{t('inventory-movement-stock-after')}</div>
              <div>
                {renderStock(
                  resultingStock,
                  `text-base font-semibold ${resultingStock < 0 ? 'text-red-500' : 'text-gray-900'}`
                )}
              </div>
            </div>
          )}
        </div>

        <FormElement label={t('inventory-field-movement-type')} required>
          <Select<InventoryMovementType>
            className="w-full"
            value={movementType}
            disabled={isSaving}
            onChange={handleTypeChange}
            optionLabelProp="label"
            options={availableTypes.map((type) => ({
              value: type,
              label: t(`inventory-mtype-${type}`),
            }))}
            // The label alone ("Give", "Adjustment") did not say what the type does
            // or which way it moves stock; the description in the option list does.
            optionRender={(option) => (
              <div className="flex flex-col py-0.5">
                <span>{option.label}</span>
                <span className="text-xs text-gray-500">{t(`inventory-mtype-${option.value}-desc`)}</span>
              </div>
            )}
          />
          <Typography.Text type="secondary" className="mt-1 block text-xs">
            {t(`inventory-mtype-${movementType}-desc`)}
          </Typography.Text>
        </FormElement>

        <FormElement label={t('inventory-mcol-quantity')} required error={errors.quantity}>
          {/* No `max`: capping at on-hand makes the box unusable whenever stock is
              0 or (from movements recorded before the backend guard existed)
              negative, because max then sits below min. The error below and the
              backend guard are what reject an over-draw. */}
          <InputNumber
            {...quantityProps}
            className="w-full"
            min={0}
            // `suffix`, not `addonAfter`: antd deprecated addonAfter on InputNumber,
            // and the unit belongs inside the field anyway.
            suffix={unitLabel ? <span className="text-xs text-gray-500">{unitLabel}</span> : undefined}
            status={errors.quantity || exceedsStock ? 'error' : undefined}
            value={quantity}
            disabled={isSaving}
            onChange={(value) => {
              setQuantity(value ?? null);
              clearError('quantity');
            }}
          />
        </FormElement>

        {movementType === 'purchase' && (
          <>
            {showCost && (
              <FormElement label={t('inventory-field-unit-cost')} required error={errors.unitCost}>
                <MoneyInput
                  className="w-full"
                  min={0}
                  status={errors.unitCost ? 'error' : undefined}
                  value={unitCost}
                  disabled={isSaving}
                  onChange={(value) => {
                    setUnitCost(value ?? null);
                    clearError('unitCost');
                  }}
                />
              </FormElement>
            )}
            <FormElement label={t('inventory-field-supplier')}>
              <Input value={supplier} disabled={isSaving} onChange={(e) => setSupplier(e.target.value)} />
            </FormElement>
          </>
        )}

        {showPrices && movementType === 'sale' && (
          <FormElement label={t('inventory-field-unit-price')} required error={errors.unitPrice}>
            <MoneyInput
              className="w-full"
              min={0}
              prefix="€"
              status={errors.unitPrice ? 'error' : undefined}
              value={unitPrice}
              disabled={isSaving}
              onChange={(value) => {
                setUnitPrice(value ?? null);
                clearError('unitPrice');
              }}
            />
          </FormElement>
        )}

        {movementType === 'rental_out' && (
          <>
            {showPrices && (
              <>
                <FormElement label={t('inventory-field-rental-rate')} required error={errors.rentalRate}>
                  <MoneyInput
                    className="w-full"
                    min={0}
                    status={errors.rentalRate ? 'error' : undefined}
                    value={rentalRate}
                    disabled={isSaving}
                    onChange={(value) => {
                      setRentalRate(value ?? null);
                      clearError('rentalRate');
                    }}
                  />
                </FormElement>
                <FormElement label={t('inventory-field-rental-rate-unit')} required>
                  <Select<RentalRateUnit>
                    className="w-full"
                    value={rentalRateUnit}
                    disabled={isSaving}
                    onChange={(value) => setRentalRateUnit(value)}
                    options={RENTAL_RATE_UNITS.map((unit) => ({
                      value: unit,
                      label: t(`inventory-rental-rate-unit-${unit}`),
                    }))}
                  />
                </FormElement>
              </>
            )}
            <FormElement label={t('inventory-field-rental-start')} required error={errors.rentalStart}>
              <DatePicker
                className="w-full"
                status={errors.rentalStart ? 'error' : undefined}
                value={rentalStart}
                disabled={isSaving}
                onChange={(value) => {
                  setRentalStart(value);
                  clearError('rentalStart');
                }}
              />
            </FormElement>
            <FormElement
              label={t('inventory-field-rental-expected-return')}
              required
              error={errors.rentalExpectedReturn}
            >
              <DatePicker
                className="w-full"
                status={errors.rentalExpectedReturn ? 'error' : undefined}
                value={rentalExpectedReturn}
                disabled={isSaving}
                disabledDate={(current) => !!rentalStart && current && current < rentalStart.startOf('day')}
                onChange={(value) => {
                  setRentalExpectedReturn(value);
                  clearError('rentalExpectedReturn');
                }}
              />
            </FormElement>
          </>
        )}

        {movementType === 'adjustment' && (
          <FormElement label={t('inventory-field-adjustment-sign')} required error={errors.adjustmentSign}>
            <Radio.Group
              value={adjustmentSign}
              disabled={isSaving}
              onChange={(e) => {
                setAdjustmentSign(e.target.value as number);
                clearError('adjustmentSign');
              }}
            >
              <Radio value={1}>{t('inventory-adjustment-increase')}</Radio>
              <Radio value={-1}>{t('inventory-adjustment-decrease')}</Radio>
            </Radio.Group>
          </FormElement>
        )}

        {showsRecipient && (
          <>
            <FormElement label={t('inventory-field-recipient-type')} required>
              <Select<InventoryRecipientType>
                className="w-full"
                value={recipientType}
                disabled={isSaving}
                onChange={(value) => {
                  setRecipientType(value);
                  // Drop the previous pick: recipientId is polymorphic, so a
                  // leftover client id would otherwise be sent as a caregiver id.
                  setRecipientId(null);
                  setSelectedClient(null);
                  setSelectedCaregiver(null);
                  setRecipientName('');
                  clearError('recipient');
                }}
                options={RECIPIENT_TYPES.map((type) => ({
                  value: type,
                  label: t(`inventory-recipient-type-${type}`),
                }))}
                optionRender={(option) => (
                  <div className="flex flex-col py-0.5">
                    <span>{option.label}</span>
                    <span className="text-xs text-gray-500">{t(`inventory-recipient-type-${option.value}-desc`)}</span>
                  </div>
                )}
                optionLabelProp="label"
              />
            </FormElement>

            {recipientType === 'client' && (
              <FormElement label={t('inventory-field-recipient-client')} required error={errors.recipient}>
                <ClientSelectGeneric
                  className="w-full"
                  allowClear
                  disabled={isSaving}
                  status={errors.recipient ? 'error' : undefined}
                  value={recipientId ?? undefined}
                  onClientChange={handleClientChange}
                  selectedItemsProvider={() => selectedClient ?? undefined}
                />
              </FormElement>
            )}

            {recipientType === 'caregiver' && (
              <FormElement label={t('inventory-field-recipient-caregiver')} required error={errors.recipient}>
                <CaregiversSelectGeneric
                  className="w-full"
                  allowClear
                  disabled={isSaving}
                  status={errors.recipient ? 'error' : undefined}
                  value={recipientId ?? undefined}
                  onCaregiverChange={handleCaregiverChange}
                  selectedItemsProvider={() => selectedCaregiver ?? undefined}
                  lazyLoadOnOpen
                />
              </FormElement>
            )}

            {recipientType === 'external' && (
              <FormElement label={t('inventory-field-recipient-name')} required error={errors.recipient}>
                <Input
                  status={errors.recipient ? 'error' : undefined}
                  placeholder={t('inventory-field-recipient-name-placeholder')}
                  value={recipientName}
                  disabled={isSaving}
                  onChange={(e) => {
                    setRecipientName(e.target.value);
                    clearError('recipient');
                  }}
                />
              </FormElement>
            )}
          </>
        )}

        {needsAdminPrice && <Alert type="warning" showIcon message={t('inventory-movement-needs-admin-price')} />}
        {showsChargeInfo && <Alert type="info" showIcon message={t('inventory-movement-charge-info')} />}

        <FormElement label={t('inventory-field-occurred-at')}>
          <DatePicker
            className="w-full"
            placeholder={t('inventory-field-occurred-at-placeholder')}
            value={occurredAt}
            disabled={isSaving}
            disabledDate={(current) => current && current > dayjs().endOf('day')}
            onChange={(value) => setOccurredAt(value)}
          />
        </FormElement>

        <FormElement label={t('inventory-mcol-note')}>
          <Input.TextArea rows={3} value={notes} disabled={isSaving} onChange={(e) => setNotes(e.target.value)} />
        </FormElement>
      </div>
    </Modal>
  );
};

export default RecordMovementModal;
